import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexLegend,
  ApexPlotOptions,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';


export interface ChartOptions {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis | ApexYAxis[];
  fill: ApexFill;
  legend: ApexLegend;
  colors?: string[];
}

@Component({
  selector: 'app-reusable-bar-chart',
  templateUrl: './reusable-bar-chart.component.html',
  styleUrl: './reusable-bar-chart.component.scss',
})
export class ReusableBarChartComponent implements OnChanges {
  @Input({ required: true }) type!: 'bar' | 'line' | 'area';
  @Input({ required: true }) series!: ApexAxisChartSeries;
  @Input() labels: string[] = [];
  @Input() horizontal = false;
  @Input() stacked = false;
  @Input() colors: string[] = [];
  @Input() height = 350;
  @Input() legendPosition: 'bottom' | 'right' | 'top' | 'left' = 'bottom';
  @Input() enableDataLabels = true;
  @Input() formatter = '';
  @Input() curve: 'smooth' | 'straight' | 'stepline' | 'monotoneCubic' = 'smooth';

  protected chartOptions: Partial<ChartOptions> = {}; 

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['series'] || changes['labels'] || changes['type']) {
      this.drawChart();
    }
  }

  private drawChart(): void {
    const isBar = this.type === 'bar';

    this.chartOptions = {
      series: this.series,
      chart: {
        type: this.type,
        height: this.height,
        stacked: this.stacked,
        toolbar: { show: true },
      },
      colors: this.colors,
      dataLabels: {
        enabled: this.enableDataLabels,
        formatter: (val) => `${val}${this.formatter}`,
      },
      stroke: isBar
        ? { width: 1, colors: ['#fff'] }
        : { width: 3, curve: this.curve },
      plotOptions: {
        bar: {
          horizontal: this.horizontal,
        },
      },
      xaxis: {
        categories: this.labels,
      },
      yaxis: {
        labels: {
          formatter: (val) => `${val}${this.formatter}`,
        },
      },
      legend: {
        show: true,
        position: this.legendPosition,
        horizontalAlign: 'center',
      },
      fill: { opacity: 1 },
      tooltip: {
        y: {
          formatter: (val) => `${val}${this.formatter}`,
        },
      },
    };
  }

  protected hasValues(): boolean {
    return this.series?.length > 0 && this.series.some(s => s.data?.length > 0);
  }
}